import { useState, useEffect, useCallback, useMemo } from 'react';
import { SingleUnit } from '../pages-equipment/equipmentTypes';
import { usePMContext } from '../../contexts/PMContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { pmSchedulingUM } from '../pages-equipment/equipmentFieldUtils';
import { lastServiceReadingBeforeAdvance } from '../../utils/reportUtils';
import { calculatePMStatus, getNextServiceInfo, getPrimaryServiceLevel, serviceLevelsOnSchedulingScale } from '../../utils/pmUtils';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { BackButton, Btn, InputField } from '../ui';
import { UMCounterLowReadingWarning } from '../UMCounterLowReadingWarning';
import { getPartsForMultipleServiceLevels, sortMatrixRows, validatePMSubmission } from '../../utils/pmFormUtils';
import {
    listServiceItemsByCompanyId,
    listServiceProcedureConsumablesByCompanyId,
    listServiceProceduresByCompanyId,
} from '../../graphql/companyResourceQueries';
import {
    buildPmInspectCostSnapshots,
    buildPmServiceExpenseInput,
    calculateEfficiencyFactor,
    getMatrixQuantityForPart,
    isPmCostTrackingEnabled,
    isPmServiceExpenseForReport,
    resolveEquipmentCategoryIdForUnit,
    summarizePmLaborEntries,
} from '../../utils/pmCostTracking';
import { createExpenseRecord, fetchExpenseRecordsByUnit } from '../../graphql/costTrackingQueries';
import { dateInputToIso } from '../../utils/costTrackingUtils';
import {
    PMConfiguration,
    PMServiceLevel,
    PMPartsAndProcedure,
    PMStatus,
} from '../../types/pmTypes';
import {
    createPMReport,
    updateServiceLevel,
} from '../../graphql/pmQueries';
import { authClient } from '../../utils/amplifyClient';
import { createDiscrepancy } from '../../graphql/mutations';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { S3_IMAGE_PATH } from '../../utils/constants';
import { ServiceDetailsSection, ServiceDetailsData } from './ServiceDetailsSection';
import { RequiredPartsSection } from './RequiredPartsSection';
import { LaborRateSection, LaborRateData } from './LaborRateSection';
import { IssuesFoundSection, IssuesFoundData } from './IssuesFoundSection';
import { SignatureCapture } from './SignatureCapture';
import '../../css/PMInspectionProcess.css';

interface PMInspectionProcessProps {
    unitId: string;
    serviceLevelIds: string[];
    onBack: () => void;
    onComplete: () => void;
}

/** Attachment for a single service item (photo + note). */
export interface ItemAttachment {
    photos: File[];
    note: string;
}

/**
 * PM Inspection Process container.
 * Header: "PM Service - [Unit Number] [Category]". Submit sits below Digital Signature.
 * Displays service level name, target UM, current UM counter, and status at top.
 * Manages form state for all inspection sections.
 *
 * Validates: Requirements 9.1, 9.2, 9.3
 */
export const PMInspectionProcess = ({
    unitId,
    serviceLevelIds,
    onBack,
    onComplete,
}: PMInspectionProcessProps) => {
    const { pmConfigurations, refreshPMData } = usePMContext();
    const { companyUnits, updateUnitInDatabase } = useEquipmentContext();
    const { companyUser, companyOwner, allCompanyUsers, company } = useUserContext();

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';

    // Resolve unit, config, and service levels
    const unit = companyUnits.find((u: SingleUnit) => u.id === unitId) as SingleUnit | undefined;
    const config = pmConfigurations.find((c: PMConfiguration) => c.unitId === unitId);
    const allConfigLevels = useMemo(
        () => config?.serviceLevels ?? [],
        [config?.serviceLevels],
    );

    const currentUM = pmSchedulingUM(unit);
    const schedulingLevels = serviceLevelsOnSchedulingScale(
        allConfigLevels,
        unit?.replacedUMCounterValue,
    );
    const nextService = schedulingLevels.length
        ? getNextServiceInfo(currentUM, schedulingLevels)
        : null;
    const dueLevels = nextService?.dueLevels;
    // Prefer the full due cascade over a crafted/under-scoped inspect URL.
    const serviceLevels = useMemo(() => {
        if (dueLevels && dueLevels.length > 0) return dueLevels;
        return allConfigLevels.filter((sl: PMServiceLevel) => serviceLevelIds.includes(sl.id));
    }, [dueLevels, allConfigLevels, serviceLevelIds]);
    const effectiveServiceLevelIds = useMemo(
        () => serviceLevels.map((sl) => sl.id),
        [serviceLevels],
    );
    const targetUM = nextService?.targetUM ?? 0;
    const dueSoonPct = config?.dueSoonPercentage ?? 0.1;
    const status = nextService
        ? calculatePMStatus({
            currentUM,
            targetUM,
            dueSoonPercentage: dueSoonPct,
            intervalUM: getPrimaryServiceLevel(allConfigLevels)?.intervalValue,
        })
        : PMStatus.SCHEDULED;

    // Matrix rows are company service items linked by pmConfigurationIds.
    // The pmConfigurationId index does not include those rows.
    const [linkedParts, setLinkedParts] = useState<PMPartsAndProcedure[]>([]);
    useEffect(() => {
        if (!companyId || !config?.id) return;
        let isCancelled = false;
        (async () => {
            try {
                const items = await listServiceItemsByCompanyId(companyId);
                if (isCancelled) return;
                setLinkedParts(
                    (items || []).filter((item) =>
                        (item.pmConfigurationIds ?? []).includes(config.id),
                    ),
                );
            } catch (err) {
                if (!isCancelled) console.error('Failed to load matrix items for PM service:', err);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId, config?.id]);

    const allParts = useMemo(() => {
        const byId = new Map<string, PMPartsAndProcedure>();
        for (const part of [...(config?.partsAndProcedures ?? []), ...linkedParts]) {
            if (part?.id) byId.set(part.id, part);
        }
        return sortMatrixRows([...byId.values()]);
    }, [config?.partsAndProcedures, linkedParts]);
    const requiredParts: PMPartsAndProcedure[] = useMemo(
        () => getPartsForMultipleServiceLevels(allParts, serviceLevels, allConfigLevels),
        [allParts, serviceLevels, allConfigLevels],
    );
    const matrixQuantities = useMemo(() => {
        const qty: Record<string, number> = {};
        for (const part of requiredParts) {
            qty[part.id] = getMatrixQuantityForPart(part.id, serviceLevels, allConfigLevels);
        }
        return qty;
    }, [requiredParts, serviceLevels, allConfigLevels]);

    // Labor category for this unit
    const laborCategoryId = config?.laborCategoryId ?? null;

    // ---- Form state ----
    const [serviceDetails, setServiceDetails] = useState<ServiceDetailsData>({
        actualUM: '',
        serviceDate: new Date().toISOString().split('T')[0],
        technicianId: '',
        technicianName: '',
    });
    const [completedItems, setCompletedItems] = useState<Record<string, boolean>>({});
    const [laborData, setLaborData] = useState<LaborRateData>({
        laborRateId: '',
        laborRateName: '',
        laborHoursST: '',
        laborHoursOT: '',
    });
    const [issuesData, setIssuesData] = useState<IssuesFoundData>({
        issueDescription: '',
        createIssueTicket: false,
    });
    const [signatureDataUrl, setSignatureDataUrl] = useState<string>('');
    const [geotag, setGeotag] = useState<{ latitude: number; longitude: number } | null>(null);
    const [geotagLabel, setGeotagLabel] = useState('Recording location…');
    const [itemAttachments, setItemAttachments] = useState<Record<string, ItemAttachment>>({});

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        let isCancelled = false;
        if (!navigator.geolocation) {
            setGeotagLabel('Location unavailable');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isCancelled) return;
                setGeotag({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
                setGeotagLabel('Location recorded');
            },
            () => {
                if (!isCancelled) setGeotagLabel('Location unavailable');
            },
            { enableHighAccuracy: true, timeout: 10000 },
        );
        return () => {
            isCancelled = true;
        };
    }, []);

    const typedActualUM = parseFloat(serviceDetails.actualUM) || 0;
    const efficiencyFactor = calculateEfficiencyFactor(typedActualUM, targetUM);

    // Keep completion map in sync when matrix rows load asynchronously.
    const requiredPartIdsKey = requiredParts.map((p) => p.id).join(',');
    useEffect(() => {
        setCompletedItems((prev) => {
            const next: Record<string, boolean> = {};
            for (const part of requiredParts) {
                next[part.id] = prev[part.id] ?? false;
            }
            return next;
        });
        // requiredParts is covered by requiredPartIdsKey
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requiredPartIdsKey]);

    // ---- Attachment helpers ----
    const handleAttachmentChange = useCallback(
        (partId: string, attachment: ItemAttachment) => {
            setItemAttachments((prev) => ({ ...prev, [partId]: attachment }));
        },
        [],
    );

    // ---- Upload photos to S3 ----
    const uploadPhotos = async (): Promise<string[]> => {
        const paths: string[] = [];
        for (const partId of Object.keys(itemAttachments)) {
            const att = itemAttachments[partId];
            for (const file of att.photos) {
                const fileName = `${uuidv4()}-PM-${file.name}`;
                const result = await uploadData({
                    path: S3_IMAGE_PATH.fullInspectionReportImagePath(companyId, fileName),
                    data: file,
                }).result;
                if (result?.path) paths.push(result.path);
            }
        }
        return paths;
    };

    // ---- Upload signature to S3 ----
    const uploadSignature = async (): Promise<string> => {
        if (!signatureDataUrl) return '';
        const blob = await (await fetch(signatureDataUrl)).blob();
        const fileName = `${uuidv4()}-PM-SIGNATURE.png`;
        const result = await uploadData({
            path: S3_IMAGE_PATH.signatureImagePath(companyId, fileName),
            data: blob,
        }).result;
        return result?.path ?? '';
    };

    // ---- Submission ----
    const handleSubmit = async () => {
        // Validate
        const validation = validatePMSubmission({
            completedItems,
            hasSignature: !!signatureDataUrl,
            actualUM: serviceDetails.actualUM,
        });
        if (!validation.isValid) {
            setSubmitError(validation.errors.join('. '));
            return;
        }

        setSubmitting(true);
        setSubmitError('');

        try {
            // Upload photos and signature
            const [photoPaths, signaturePath] = await Promise.all([
                uploadPhotos(),
                uploadSignature(),
            ]);

            const laborSummary = summarizePmLaborEntries(
                (laborData.entries ?? []).map((entry) => ({
                    categoryId: entry.categoryId,
                    categoryName: entry.categoryName,
                    standardRate: entry.standardRate,
                    overtimeRate: entry.overtimeRate,
                    hoursST: parseFloat(entry.hoursST) || 0,
                    hoursOT: parseFloat(entry.hoursOT) || 0,
                })),
            );
            const stHours = laborSummary.laborHoursST;
            const otHours = laborSummary.laborHoursOT;
            const stRate = laborSummary.laborRateST;
            const otRate = laborSummary.laborRateOT;
            const totalLaborCost = laborSummary.totalLaborCost;

            const actualUM = parseFloat(serviceDetails.actualUM) || 0;

            // Build report data JSON (items + attachments)
            const reportData = JSON.stringify({
                items: requiredParts.map((p) => ({
                    partId: p.id,
                    itemName: p.itemName,
                    itemType: p.itemType ?? null,
                    manufacturer: p.manufacturer ?? null,
                    partNumber: p.partNumber ?? null,
                    completed: completedItems[p.id] ?? false,
                    quantity: matrixQuantities[p.id] ?? p.quantity ?? null,
                    note: itemAttachments[p.id]?.note ?? '',
                    photoCount: itemAttachments[p.id]?.photos?.length ?? 0,
                })),
                efficiencyFactor,
                targetUM,
                laborLines: laborSummary.lines,
                geotag,
            });

            // Create PMReport
            const reportResult = await createPMReport({
                companyId,
                unitId,
                pmConfigurationId: config!.id,
                serviceLevelId: effectiveServiceLevelIds[0],
                serviceLevelName: serviceLevels.map(sl => sl.levelIdentifier).join(', '),
                actualUM,
                serviceDate: dateInputToIso(serviceDetails.serviceDate),
                technicianId: serviceDetails.technicianId,
                technicianName: serviceDetails.technicianName,
                laborRateScheduleId: laborSummary.laborRateScheduleId || undefined,
                laborRateScheduleName: laborSummary.laborRateScheduleName || undefined,
                laborHoursST: stHours,
                laborHoursOT: otHours,
                laborRateST: stRate,
                laborRateOT: otRate,
                totalLaborCost,
                reportData,
                issueDescription: issuesData.issueDescription || undefined,
                issueTicketCreated: issuesData.createIssueTicket,
                signature: signaturePath || undefined,
                photos: photoPaths.length > 0 ? photoPaths : undefined,
                status: 'Completed',
            });

            const reportId = reportResult?.id;

            // Cost Tracking: the completed service, plus checked consumable and labor dollars
            if (
                reportId
                && isPmCostTrackingEnabled(company, unit)
            ) {
                try {
                    let procedureCosts = null;
                    try {
                        const [procedures, links, catalog] = await Promise.all([
                            listServiceProceduresByCompanyId(companyId),
                            listServiceProcedureConsumablesByCompanyId(companyId),
                            listServiceItemsByCompanyId(companyId),
                        ]);
                        procedureCosts = {
                            procedures,
                            links,
                            catalog: (catalog || []) as PMPartsAndProcedure[],
                        };
                    } catch (procedureError) {
                        console.error('Failed to load procedure consumables for PM cost:', procedureError);
                    }
                    const snapshots = buildPmInspectCostSnapshots(
                        requiredParts,
                        serviceLevels,
                        completedItems,
                        procedureCosts,
                        allConfigLevels,
                    );
                    const expenseInput = buildPmServiceExpenseInput({
                        companyId,
                        unitId,
                        equipmentCategoryId: resolveEquipmentCategoryIdForUnit(unit),
                        reportId,
                        dateOfService: dateInputToIso(serviceDetails.serviceDate),
                        serviceLevelId: effectiveServiceLevelIds[0],
                        serviceLevelName: serviceLevels.map((sl) => sl.levelIdentifier).join(', '),
                        snapshots,
                        laborHoursST: stHours,
                        laborHoursOT: otHours,
                        laborRateST: stRate,
                        laborRateOT: otRate,
                        laborRateScheduleId: laborSummary.laborRateScheduleId || null,
                        laborRateScheduleName: laborSummary.laborRateScheduleName || null,
                    });
                    if (expenseInput) {
                        const existing = await fetchExpenseRecordsByUnit(unitId);
                        const alreadyRecorded = (existing || []).some((expense) =>
                            isPmServiceExpenseForReport(expense || {}, reportId),
                        );
                        if (!alreadyRecorded) {
                            await createExpenseRecord(expenseInput);
                        }
                    }
                } catch (ctError) {
                    console.error('Failed to record PM Cost Tracking expense:', ctError);
                }
            }

            // Update service level baselines only when a real actual UM was recorded
            if (actualUM > 0) {
                for (const slId of effectiveServiceLevelIds) {
                    await updateServiceLevel({
                        id: slId,
                        lastServiceUM: actualUM,
                        lastServiceDate: new Date().toISOString(),
                    });
                }

                // Ratchet face UM: max(Inspection, DS, PM actual) — never last-write-wins lower
                const previousFace = unit?.currentUMCounterValue;
                const ratcheted = Math.max(actualUM, previousFace ?? 0);
                const unitUpdates: Partial<SingleUnit> = {
                    currentUMCounterValue: ratcheted,
                };
                const lastService = lastServiceReadingBeforeAdvance(previousFace, actualUM);
                if (lastService != null) {
                    unitUpdates.lastServiceHours = lastService;
                }
                await updateUnitInDatabase(unitId, unitUpdates);
            }

            // Conditionally create Discrepancy (issue ticket)
            if (issuesData.createIssueTicket && issuesData.issueDescription) {
                const discrepancyResult = await authClient.graphql({
                    query: createDiscrepancy,
                    variables: {
                        input: {
                            companyId,
                            equipmentId: unit?.categoryId ?? unitId,
                            unitId,
                            reportId: reportId ?? undefined,
                            equipmentName: unit?.name ?? 'Unknown',
                            status: 'warn',
                            initialStatus: 'warn',
                            log: JSON.stringify([{
                                name: serviceDetails.technicianName || 'PM Service',
                                notes: issuesData.issueDescription,
                                date: new Date().toISOString(),
                                source: 'PM',
                            }]),
                            promptDescription: issuesData.issueDescription,
                            promptTitle: `PM Issue - ${serviceLevels.map(sl => sl.levelIdentifier).join(', ') || 'Service'}`,
                            promptLabel: 'PM Inspection Issue',
                        },
                    },
                });
                const discrepancyId =
                    discrepancyResult?.data?.createDiscrepancy?.id;

                // Store issueTicketId on the PMReport
                if (discrepancyId && reportId) {
                    const { updatePMReport: updatePMReportMutation } = await import(
                        '../../graphql/pmQueries'
                    );
                    await updatePMReportMutation({
                        id: reportId,
                        issueTicketId: discrepancyId,
                    });
                }
            }

            await refreshPMData();
            onComplete();
        } catch (err) {
            console.error('PM submission error:', err);
            setSubmitError('Failed to submit PM service. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ---- Status badge helper ----
    const statusClass =
        status === PMStatus.OVERDUE
            ? 'overdue'
            : status === PMStatus.DUE_SOON
                ? 'due-soon'
                : 'scheduled';

    const categoryLabel =
        typeof unit?.category === 'string'
            ? unit.category
            : typeof unit?.category?.name === 'string'
              ? unit.category.name
              : '';
    const unitLabel = unit ? `${unit.name} ${categoryLabel}`.trim() : unitId;

    return (
        <div>
            {/* Exit link */}
            <div className="pm-no-print">
            <BackButton onClick={onBack} buttonText="Exit PM Service" />
            </div>

            {/* Header */}
            <div className="pm-inspection-header pm-no-print">
                <h2>PM Service - {unitLabel}</h2>
            </div>

            {/* Service level info bar */}
            <div className="pm-inspection-info-bar pm-no-print">
                <div className="pm-inspection-info-item">
                    <span className="pm-inspection-info-label">Service Level</span>
                    <span className="pm-inspection-info-value">
                        {serviceLevels.length > 0
                            ? serviceLevels.map(sl => sl.levelIdentifier).join(', ')
                            : '—'}
                    </span>
                </div>
                <div className="pm-inspection-info-item">
                    <span className="pm-inspection-info-label">Target UM</span>
                    <span className="pm-inspection-info-value">
                        {targetUM} {displayUnitOfMeasure(config?.unitOfMeasure)}
                    </span>
                </div>
                <div className="pm-inspection-info-item">
                    <span className="pm-inspection-info-label">Current UM</span>
                    <span className="pm-inspection-info-value">
                        {currentUM} {displayUnitOfMeasure(config?.unitOfMeasure)}
                    </span>
                </div>
                <div className="pm-inspection-info-item pm-inspection-actual-um">
                    <label className="pm-inspection-info-label" htmlFor="pm-actual-um">
                        Actual UM at Time of Service
                        <span className="pm-inspection-required-mark" aria-hidden="true"> *</span>
                    </label>
                    <InputField
                        inputId="pm-actual-um"
                        type="number"
                        placeholder="Required"
                        ariaLabel="Actual UM at Time of Service, required"
                        value={serviceDetails.actualUM}
                        onChange={(actualUM) => setServiceDetails((prev) => ({ ...prev, actualUM }))}
                        min="0"
                        step="any"
                        hasError={Boolean(submitError) && !(parseFloat(serviceDetails.actualUM) > 0)}
                        suffix={displayUnitOfMeasure(config?.unitOfMeasure ?? unit?.unitOfMeasure)}
                    />
                    <UMCounterLowReadingWarning
                        enteredValue={serviceDetails.actualUM}
                        unit={unit}
                        unitOfMeasure={config?.unitOfMeasure ?? unit?.unitOfMeasure}
                    />
                </div>
                <div className="pm-inspection-info-item">
                    <span className="pm-inspection-info-label">Status</span>
                    <span className={`pm-status-badge ${statusClass}`}>{status}</span>
                </div>
                {efficiencyFactor != null && (
                    <div className="pm-inspection-info-item">
                        <span className="pm-inspection-info-label">Over/Under Target %</span>
                        <span className="pm-inspection-info-value">
                            {efficiencyFactor.toFixed(1)}%
                        </span>
                    </div>
                )}
            </div>

            {/* Sections */}
            <div className="pm-no-print">
            <ServiceDetailsSection
                data={serviceDetails}
                onChange={setServiceDetails}
                users={allCompanyUsers}
            />
            </div>

            <RequiredPartsSection
                parts={requiredParts}
                quantities={matrixQuantities}
                unitLabel={unitLabel}
                serviceLevelLabel={
                    serviceLevels.length > 0
                        ? serviceLevels.map((sl) => sl.levelIdentifier).join(', ')
                        : '—'
                }
                completedItems={completedItems}
                onToggle={(partId) =>
                    setCompletedItems((prev) => ({ ...prev, [partId]: !prev[partId] }))
                }
                onSetAllCompleted={(completed) =>
                    setCompletedItems(
                        Object.fromEntries(requiredParts.map((part) => [part.id, completed])),
                    )
                }
                attachments={itemAttachments}
                onAttachmentChange={handleAttachmentChange}
            />

            <div className="pm-no-print">
            <LaborRateSection
                laborCategoryId={laborCategoryId}
                data={laborData}
                onChange={setLaborData}
            />

            <IssuesFoundSection data={issuesData} onChange={setIssuesData} />
            <p className="pm-inspection-geotag">{geotagLabel}</p>

            <SignatureCapture
                signatureDataUrl={signatureDataUrl}
                onCapture={setSignatureDataUrl}
            />

            <div className="pm-inspection-submit-actions">
                {submitError && (
                    <div className="pm-inspection-error pm-inspection-submit-error" role="alert">
                        {submitError}
                    </div>
                )}
                <Btn
                    variant="primary"
                    buttonText={submitting ? 'Submitting…' : 'Submit PM Service'}
                    buttonOnClick={handleSubmit}
                    buttonDisabled={submitting}
                    buttonAriaProps={submitting ? { 'aria-busy': true } : undefined}
                />
            </div>
            </div>
        </div>
    );
};
